import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, FileText, Users, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PreencherRelatorioModal } from "@/components/rdo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WorkHeader } from "@/components/dashboard/WorkHeader";
import { StatsCards } from "@/components/dashboard/StatsCards";
import { TasksAndSuggestions } from "@/components/dashboard/TasksAndSuggestions";
import { AttendanceChart } from "@/components/dashboard/AttendanceChart";
import { WorkChart } from "@/components/dashboard/WorkChart";
import { PhotoGallery } from "@/components/dashboard/PhotoGallery";
import { RecentReports } from "@/components/dashboard/RecentReports";
import { TaskList } from "@/components/dashboard/TaskList";
import { 
  WorkDashboardItemsSheet, 
  WorkDashboardItem, 
  loadWorkDashboardLayout 
} from "./WorkDashboardItemsSheet";
import { useObraReportDefaults } from "@/hooks/useObraReportDefaults";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getObraById } from "@/lib/obraCache";
import { getClienteNomeById } from "@/utils/clientesData";

type WorkHeader = {
  name: string;
  client: string;
  city: string;
  progress: number;
  daysRemaining: number;
  status: "active" | "critical";
};

// Obra real do store hidratado do Supabase
function loadObraLocal(id: string) {
  try {
    return getObraById(id) || null;
  } catch (error) {
    console.error("Error loading obra from cache:", error);
    return null;
  }
}

function loadReportDefaultsLocal(id: string) {
  // Report defaults vêm do registro da obra (Supabase, via obraCache hidratado).
  return getObraById(id)?.reportDefaults ?? null;
}

// Helper function to parse dates - handles both Date objects and ISO strings
function parseDate(dateValue: any): Date | null {
  if (!dateValue) return null;
  
  try {
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // If it's a string (ISO format)
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }
    
    // If it's an object with date properties (from form)
    if (typeof dateValue === 'object' && dateValue !== null) {
      // Check for various possible date object structures
      if (dateValue.toISOString) {
        return new Date(dateValue.toISOString());
      }
      // Handle other possible structures as needed
    }
    
    return null;
  } catch (error) {
    console.warn("Error parsing date:", dateValue, error);
    return null;
  }
}

// Map local obra to WorkHeader format
function mapObraToWorkHeader(obra: any): WorkHeader {
  const statusMap: Record<string, "active" | "critical"> = {
    "em-andamento": "active",
    "concluido": "active", 
    "planejamento": "active",
    "pausado": "critical",
    "cancelado": "critical",
  };
  
  // Parse the end date properly
  const endDate = parseDate(obra?.previsaoTermino);
  const daysRemaining = endDate
    ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86400000))
    : 0;

  // Resolve client ID to name
  const clientName = obra?.clienteObra ? getClienteNomeById(obra.clienteObra) : "Cliente";

  return {
    name: obra?.nomeObra ?? "Obra",
    client: clientName,
    city: obra?.endereco?.cidade ?? "",
    progress: Number(obra?.progresso ?? obra?.progress ?? 0),
    daysRemaining,
    status: statusMap[obra?.statusObra] ?? "active",
  };
}

export const WorkPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const workId = searchParams.get('obra') ?? '';

  // Fonte única: obra real do Supabase (via obraCache). Sem fallback mock —
  // obra inexistente cai no estado de erro renderizado mais abaixo.
  const obraLocal = workId ? loadObraLocal(workId) : null;
  const workData = obraLocal ? mapObraToWorkHeader(obraLocal) : null;
  
  const [isItemsSheetOpen, setIsItemsSheetOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [dashboardItems, setDashboardItems] = useState<WorkDashboardItem[]>([]);
  const [workHeaderMinimized, setWorkHeaderMinimized] = useState(false);
  
  // Load report defaults: prefer local cache, fallback to hook
  const localDefaults = loadReportDefaultsLocal(workId);
  const { reportDefaults: hookDefaults, isLoading: isLoadingDefaults, error: defaultsError } = useObraReportDefaults(workId);
  const reportDefaults = localDefaults || hookDefaults;

  useEffect(() => {
    const items = loadWorkDashboardLayout(workId);
    setDashboardItems(items);
    
    // Load workHeader minimized state separately
    const savedWorkHeaderMinimized = localStorage.getItem(`workHeader:${workId}:minimized`);
    if (savedWorkHeaderMinimized) {
      setWorkHeaderMinimized(JSON.parse(savedWorkHeaderMinimized));
    }
  }, [workId]);

  const handleBack = () => {
    // Restore previous filters from localStorage if they exist
    const savedFilters = localStorage.getItem('obras-filters');
    if (savedFilters) {
      navigate(`/obras?${savedFilters}`);
    } else {
      navigate('/obras');
    }
  };

  const handleApplyItems = (items: WorkDashboardItem[]) => {
    setDashboardItems(items);
  };

  const handleCloseCard = (cardId: string) => {
    const updatedItems = dashboardItems.map(item => {
      if (item.id === 'statsCards' && item.subItems) {
        const updatedSubItems = item.subItems.map(subItem =>
          subItem.id === cardId ? { ...subItem, visible: false } : subItem
        );
        return { ...item, subItems: updatedSubItems };
      }
      return item;
    });
    
    setDashboardItems(updatedItems);
    
    // Persist to localStorage
    const layout = {
      obraId: workId,
      items: updatedItems
    };
    localStorage.setItem(`dashboardLayout:${workId}`, JSON.stringify(layout));
  };

  const handleCloseItem = (itemId: string) => {
    const updatedItems = dashboardItems.map(item =>
      item.id === itemId ? { ...item, visible: false } : item
    );
    
    setDashboardItems(updatedItems);
    
    // Persist to localStorage
    const layout = {
      obraId: workId,
      items: updatedItems
    };
    localStorage.setItem(`dashboardLayout:${workId}`, JSON.stringify(layout));
  };

  const handleMinimizeItem = (itemId: string) => {
    // Handle workHeader separately since it's not in dashboardItems
    if (itemId === 'workHeader') {
      setWorkHeaderMinimized(!workHeaderMinimized);
      localStorage.setItem(`workHeader:${workId}:minimized`, JSON.stringify(!workHeaderMinimized));
      return;
    }

    const updatedItems = dashboardItems.map(item =>
      item.id === itemId ? { ...item, minimized: !item.minimized } : item
    );
    
    setDashboardItems(updatedItems);
    
    // Persist to localStorage  
    const layout = {
      obraId: workId,
      items: updatedItems
    };
    localStorage.setItem(`dashboardLayout:${workId}`, JSON.stringify(layout));
  };

  const getItemById = (id: string) => {
    return dashboardItems.find(item => item.id === id);
  };

  const isVisible = (id: string) => {
    const item = getItemById(id);
    return item ? item.visible : true;
  };

  const isMinimized = (id: string) => {
    // Handle workHeader separately since it's not in dashboardItems
    if (id === 'workHeader') {
      return workHeaderMinimized;
    }
    
    const item = getItemById(id);
    return item ? item.minimized || false : false;
  };

  // Check if section is enabled based on report defaults
  const isSectionEnabled = (sectionKey: string): boolean => {
    if (!reportDefaults) return true; // Fallback: show all sections
    
    const sectionMapping: Record<string, keyof typeof reportDefaults> = {
      'weather': 'condicoesClimaticas',
      'workforce': 'maoDeObra',
      'equipment': 'equipamentos',
      'activities': 'atividades',
      'occurrences': 'ocorrencias',
      'materials': 'materiais',
      'comments': 'comentarios',
      'photos': 'fotos',
      'videos': 'videos',
      'attachments': 'anexos'
    };
    
    const mappedKey = sectionMapping[sectionKey];
    return mappedKey ? reportDefaults[mappedKey] : true;
  };

  // Filter dashboard items based on enabled sections
  const getFilteredDashboardItems = (): WorkDashboardItem[] => {
    if (!reportDefaults) return dashboardItems; // Show all if not loaded yet
    
    return dashboardItems.filter(item => {
      // Handle specific mappings for dashboard items to report sections
      switch (item.id) {
        case 'photoGallery':
          return isSectionEnabled('photos');
        case 'recentReports':
          return isSectionEnabled('comments'); // Reports are part of comments/timeline
        case 'taskList':
          return isSectionEnabled('activities'); // Tasks relate to activities
        case 'attendanceChart':
        case 'workChart':
          return isSectionEnabled('workforce'); // Charts relate to workforce
        case 'tasksAndSuggestions':
          return isSectionEnabled('activities');
        default:
          return true; // Keep statsCards and other general items
      }
    });
  };

  // Sort items by order for rendering
  const sortedItems = getFilteredDashboardItems().sort((a, b) => a.order - b.order);

  // Obra inexistente (URL antiga, id inválido ou obra removida): estado claro,
  // sem fallback para dados de exemplo.
  if (!obraLocal || !workData) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          className="text-muted-foreground hover:text-foreground mb-6"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <Alert>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              Obra não encontrada{workId ? ` (id: ${workId})` : ""}. Ela pode ter
              sido removida ou o link está desatualizado.
            </span>
            <Button size="sm" className="h-11 sm:h-9" onClick={() => navigate("/obras")}>
              Ver minhas obras
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header with Back button, Items button and Add button */}
      <div className="flex items-center justify-between mb-4">
        <Button 
          variant="ghost" 
          className="text-muted-foreground hover:text-foreground"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setIsItemsSheetOpen(true)}
            className="gap-2 h-11 sm:h-9"
          >
            <List className="h-4 w-4" />
            Itens
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="default" size="sm" className="gap-2 h-11 sm:h-9">
                <Plus className="w-4 h-4" />
                Adicionar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => setIsReportModalOpen(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Preencher RDO
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <Users className="h-4 w-4 mr-2" />
                Adicionar Usuário
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Work Header - específico da obra */}
      {isVisible('workHeader') && (
        <WorkHeader
          workName={workData.name}
          client={workData.client}
          progress={workData.progress}
          daysRemaining={workData.daysRemaining}
          status={workData.status}
          obraId={workId}
          onMinimize={() => handleMinimizeItem('workHeader')}
          isMinimized={isMinimized('workHeader')}
        />
      )}

      <div className="space-y-6 mt-6">
        {/* Loading state for report defaults - only show if not using local cache */}
        {!localDefaults && isLoadingDefaults && (
          <div className="space-y-6">
            <Skeleton className="h-32 w-full" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <Skeleton className="h-48 w-full" />
          </div>
        )}
        
        {/* Error state */}
        {!localDefaults && defaultsError && (
          <Alert>
            <AlertDescription>
              {defaultsError}. Exibindo todas as seções como fallback.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Render dashboard items */}
        {(!isLoadingDefaults || localDefaults) && sortedItems.map(item => {
          if (!item.visible) return null;
          
          switch (item.id) {
            case 'statsCards':
              const statsItem = dashboardItems.find(item => item.id === 'statsCards');
              const subItemsVisibility = {
                reports: statsItem?.subItems?.find(sub => sub.id === 'statsCards.reports')?.visible !== false,
                occurrences: statsItem?.subItems?.find(sub => sub.id === 'statsCards.occurrences')?.visible !== false,
                workforce: statsItem?.subItems?.find(sub => sub.id === 'statsCards.workforce')?.visible !== false,
                equipment: statsItem?.subItems?.find(sub => sub.id === 'statsCards.equipment')?.visible !== false,
                photos: statsItem?.subItems?.find(sub => sub.id === 'statsCards.photos')?.visible !== false,
                attachments: statsItem?.subItems?.find(sub => sub.id === 'statsCards.attachments')?.visible !== false,
                approveRdos: statsItem?.subItems?.find(sub => sub.id === 'statsCards.approveRdos')?.visible !== false,
              };
              return <StatsCards 
                key={item.id} 
                visibleSubItems={subItemsVisibility} 
                onCloseCard={handleCloseCard}
                workContext={{
                  obraId: workId,
                  obraName: workData.name
                }}
              />;
            
            case 'attendanceChart':
              return (
                <div key="attendance-chart" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <AttendanceChart
                    obraId={workId}
                    onClose={() => handleCloseItem('attendanceChart')}
                    onMinimize={() => handleMinimizeItem('attendanceChart')}
                    isMinimized={isMinimized('attendanceChart')}
                  />
                  {isVisible('workChart') && (
                    <WorkChart
                      obraId={workId}
                      onClose={() => handleCloseItem('workChart')}
                      onMinimize={() => handleMinimizeItem('workChart')}
                      isMinimized={isMinimized('workChart')}
                    />
                  )}
                </div>
              );
            
            case 'workChart':
              // Only render if attendanceChart is not visible (to avoid duplicate rendering)
              if (!isVisible('attendanceChart')) {
                return (
                  <div key="work-chart" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <WorkChart
                      obraId={workId}
                      onClose={() => handleCloseItem('workChart')}
                      onMinimize={() => handleMinimizeItem('workChart')}
                      isMinimized={isMinimized('workChart')}
                    />
                  </div>
                );
              }
              return null;
            
            case 'tasksAndSuggestions':
              return (
                <TasksAndSuggestions 
                  key={item.id}
                  workId={workId}
                  onClose={() => handleCloseItem('tasksAndSuggestions')}
                  onMinimize={() => handleMinimizeItem('tasksAndSuggestions')}
                  isMinimized={isMinimized('tasksAndSuggestions')}
                />
              );
            
            case 'photoGallery':
              return (
                <PhotoGallery
                  key={item.id}
                  obraId={workId}
                  onClose={() => handleCloseItem('photoGallery')}
                  onMinimize={() => handleMinimizeItem('photoGallery')}
                  isMinimized={isMinimized('photoGallery')}
                />
              );
            
            case 'recentReports':
            case 'taskList':
              // Group reports and tasks together if both are visible
              const reportsVisible = isVisible('recentReports');
              const tasksVisible = isVisible('taskList');
              
              if (item.id === 'recentReports' && (reportsVisible || tasksVisible)) {
                return (
                  <div key="reports-tasks-row" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {reportsVisible && (
                      <RecentReports
                        obraId={workId}
                        onClose={() => handleCloseItem('recentReports')}
                        onMinimize={() => handleMinimizeItem('recentReports')}
                        isMinimized={isMinimized('recentReports')}
                      />
                    )}
                    {tasksVisible && (
                      <TaskList 
                        workId={workId}
                        onClose={() => handleCloseItem('taskList')}
                        onMinimize={() => handleMinimizeItem('taskList')}
                        isMinimized={isMinimized('taskList')}
                      />
                    )}
                  </div>
                );
              }
              return null;
            
            default:
              return null;
          }
        })}
      </div>

      {/* Work Dashboard Items Sheet */}
      <WorkDashboardItemsSheet
        open={isItemsSheetOpen}
        onOpenChange={setIsItemsSheetOpen}
        obraId={workId}
        items={getFilteredDashboardItems()}
        onApply={handleApplyItems}
      />

      {/* Preencher Relatório Modal */}
      <PreencherRelatorioModal
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        defaultObra={workId}
      />
    </div>
  );
};